import React from 'react';
import { GalleryImageProps } from '../../models/GalleryWidgetModel';
import { LeftArrow, RightArrow } from 'shared-widget-components/src/components/Icons';
import { CDNImage } from 'shared-widget-components/src/components/CDNImage/CDNImage';
import { useKeyboardNavigation } from './hooks/useKeyboardNavigation';

interface FullscreenImagesProps {
  isViewerOpen: boolean;
  viewerImageIndex: number | null;
  closeImageViewer: () => void;
  images: GalleryImageProps[];
  showPreviousImage: () => void;
  showNextImage: () => void;
}

/**
 * @description This component displays and handles the fullscreen mode of the gallery images
 * @param0 isViewerOpen boolean; This boolean value indicates if the viewer is open or not.
 * @param1 viewerImageIndex number | null; The index of the image that is currently displayed in the viewer.
 * @param2 closeImageViewer () => void; This function is called when the viewer is closed.
 * @param3 widgetState GalleryWidgetState; The data of the widget.
 * @param4 showPreviousImage () => void; This function is called to show the previous image.
 * @param5 showNextImage () => void; This function is called to show the next image.
 * @returns {JSX.Element} The FullscreenImages component
 */

export const FullscreenImages: React.FC<FullscreenImagesProps> = ({ isViewerOpen, viewerImageIndex, closeImageViewer, images, showPreviousImage, showNextImage }) => {
  useKeyboardNavigation({ isViewerOpen, showPreviousImage, showNextImage, closeImageViewer });

  return (
    <>
      {isViewerOpen && viewerImageIndex !== null && (
        <div className='fixed inset-0 flex items-center justify-center z-[100]'>
          <div className='fixed inset-0 bg-black opacity-80' onClick={closeImageViewer}></div>
          <div className='max-w-[1240px] w-fit max-h-[400px] min-[350px]:mx-6 sm:mx-2 min-[350px]:h-fit sm:h-full z-50 relative flex items-center' onClick={(e) => e.stopPropagation()}>
            {images[viewerImageIndex] && images[viewerImageIndex].fileName !== '' && (
              <CDNImage src={images[viewerImageIndex].url!} alt={images[viewerImageIndex].alt} className='z-50' imageWidth={1080} imageHeight={600} mobileWidth={540} mobileHeight={300} fit='crop' />
            )}
            {images.length > 1 && (
              <button
                type='button'
                className='p-3 absolute top-[50%] -left-5 rounded-full bg-white text-black z-50 hover:bg-gray-200 transform -translate-y-1/2 outline-none focus:border-none focus:outline-none'
                onClick={(e: React.MouseEvent) => {
                  showPreviousImage();
                  e.stopPropagation();
                }}
              >
                <LeftArrow />
              </button>
            )}
            {images.length > 1 && (
              <button
                type='button'
                className='p-3 absolute top-[50%] -right-5 rounded-full bg-white text-black z-50 hover:bg-gray-200 transform -translate-y-1/2 outline-none focus:border-none focus:outline-none'
                onClick={(e: React.MouseEvent) => {
                  showNextImage();
                  e.stopPropagation();
                }}
              >
                <RightArrow />
              </button>
            )}
          </div>
          <button type='button' className='p-2 px-2 absolute top-4 right-8 bg-transparent text-white hover:bg-gray-200' onClick={closeImageViewer}>
            x
          </button>
        </div>
      )}
    </>
  );
};
